import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import brand from '@/brand';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'react-data-table-component';
import { Card, CardBody, CardHeader, CardTitle, Container } from 'reactstrap';
import SalesFilters from '../components/SalesFilters';
import { ISale } from '@/types/reports';
import dayjs from 'dayjs';
import useSalesReportsHook from '@/hooks/useSalesReportsHook';
import PaginationComponent from '@/components/Paginate';
import { getSalesReport } from '@/api/reports';
import Loader from '@/components/Loader';
import { Link } from 'react-router-dom';
import EmptyState from '@/components/EmptyState';

const columns = [
  {
    name: 'ID',
    label: 'id',
    selector: (row: ISale) => row.id,
    sortable: true,
  },
  {
    name: 'Data',
    label: 'created_at',
    selector: (row: ISale) => dayjs(row.created_at).format('DD-MM-YYYY HH:mm'),
    sortable: true,
  },
  {
    name: 'Utilizator',
    label: 'name',
    selector: (row: ISale) => row?.name,
    format: (row: ISale) => (
      <Link to={`/app/users/${row.user_uuid}`}>{row.name}</Link>
    ),
    sortable: true,
  },
  {
    name: 'Tip Utilizator',
    label: 'is_business',
    selector: (row: ISale) => (row.is_business ? 'Companie' : 'P. Fizica'),
    sortable: false,
  },
  {
    name: 'Varsta',
    label: 'birth_date',
    selector: (row: ISale) =>
      row?.birth_date ? dayjs().diff(row?.birth_date, 'years') + ' ani' : 'n/a',
    sortable: true,
  },
  {
    name: 'Magazin',
    selector: (row: ISale) => row.location_name,
  },
  {
    name: 'Puncte',
    label: 'points',
    selector: (row: any) => (row.points > 0 ? '+' + row.points : row.points),
    sortField: 'points',
  },
  {
    name: 'Platit',
    selector: (row: ISale) => row.amount,
  },
  {
    name: 'Tip Vanzare',
    selector: (row: ISale) =>
      row.type === 'INCOME' ? 'Obtine puncte' : 'Foloseste puncte',
  },
  {
    name: 'Mod Obtinere',
    selector: (row: ISale) => row?.description,
  },
];

const SalesReportPage = () => {
  const {
    loading,
    salesData,
    page,
    perPage,
    total,
    changePage,
    changePerPage,
    changeSort,
    totalSales,
    salesQuery,
  } = useSalesReportsHook(getSalesReport);

  const handleSort = (column: (typeof columns)[0], sortDirection: string) => {
    changeSort(column.label, sortDirection);
  };

  useEffect(() => {
    salesQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <Helmet>
        <title>Raport vanzari - {brand.name}</title>
      </Helmet>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title={brand.shortName}
            breadcrumbItem="Raport vanzari"
          />
          {/*<DailyPointsChart*/}
          {/*  data={data}*/}
          {/*  startDate={filters.startDate}*/}
          {/*  endDate={filters.endDate}*/}
          {/*/>*/}
          <SalesFilters />
          <Card>
            <CardHeader className="bg-info">
              <CardTitle className="text-white">Raport vanzari</CardTitle>
            </CardHeader>
            <CardBody className="card-body">
              {loading && <Loader loading={loading} />}
              {!loading && salesData?.length === 0 && (
                <EmptyState
                  title="Nu exista date"
                  description="Nu s-au gasit date pentru filtrele selectate."
                />
              )}
              {!loading && salesData?.length > 0 && (
                <>
                  <DataTable
                    columns={columns}
                    data={salesData}
                    dense
                    onSort={handleSort}
                    sortServer={true}
                  />
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      marginTop: '15px',
                    }}
                  >
                    Total vanzari: {totalSales} RON
                  </div>
                  <div className="d-flex justify-content-center align-items-center p-3">
                    <PaginationComponent
                      page={page}
                      perPage={perPage}
                      total={total}
                      onClick={(p) => changePage(p)}
                    />
                    <div
                      className={`form-group d-flex align-items-center ${
                        total < 11 ? 'd-none' : ''
                      }`}
                    >
                      <select
                        className="form-control m-2"
                        id="perPage"
                        style={{ width: '50px' }}
                        value={perPage}
                        onChange={(e) =>
                          changePerPage(parseInt(e.target.value))
                        }
                      >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="30">30</option>
                        <option value="40">40</option>
                        <option value="50">50</option>
                      </select>
                      {'   '} - pe pagina din
                      <strong className={'p-1'}>{total}</strong>
                      inregistrari
                    </div>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SalesReportPage;
